import { useState } from "react";
import { MdOutlineClose } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { CartItem } from "../../models/cart-item.ts";
import { OrderStatus } from "../../models/order-status.ts";
import { Order } from "../../models/order.ts";
import { createOrder } from "../../Services/order.service.ts";
import {
  removeFromCart,
  updateCartItemQuantity,
} from "../../store/actions/cartActions.ts";
import { RootState } from "../../store/store.ts";
import BarSpinner from "../Loader/BarSpinner.tsx";
import "./ShoppingCart.css";

const PAKISTAN_CITIES = [
  "Abbottabad",
  "Ahmadpur East",
  "Alipur",
  "Arifwala",
  "Attock",
  "Badin",
  "Bagh",
  "Bahawalnagar",
  "Bahawalpur",
  "Bannu",
  "Battagram",
  "Bhakkar",
  "Bhalwal",
  "Bhimber",
  "Burewala",
  "Chakwal",
  "Chaman",
  "Charsadda",
  "Chichawatni",
  "Chiniot",
  "Chishtian",
  "Chitral",
  "Dadu",
  "Daharki",
  "Daska",
  "Dera Ghazi Khan",
  "Dera Ismail Khan",
  "Faisalabad",
  "Ghotki",
  "Gilgit",
  "Gojra",
  "Gujranwala",
  "Gujrat",
  "Gwadar",
  "Hafizabad",
  "Hangu",
  "Haripur",
  "Hasilpur",
  "Hub",
  "Hunza",
  "Hyderabad",
  "Islamabad",
  "Jacobabad",
  "Jaranwala",
  "Jatoi",
  "Jhang",
  "Jhelum",
  "Kamalia",
  "Kamoke",
  "Kandhkot",
  "Karachi",
  "Karak",
  "Kasur",
  "Khairpur",
  "Khanewal",
  "Khanpur",
  "Kharian",
  "Khushab",
  "Khuzdar",
  "Kohat",
  "Kot Addu",
  "Kotli",
  "Kotri",
  "Lahore",
  "Lakki Marwat",
  "Larkana",
  "Layyah",
  "Lodhran",
  "Loralai",
  "Mailsi",
  "Mandi Bahauddin",
  "Mansehra",
  "Mardan",
  "Mastung",
  "Matiari",
  "Mian Channu",
  "Mianwali",
  "Mirpur",
  "Mirpur Khas",
  "Multan",
  "Muridke",
  "Muzaffarabad",
  "Muzaffargarh",
  "Narowal",
  "Nawabshah",
  "Nowshera",
  "Okara",
  "Pakpattan",
  "Pasni",
  "Pattoki",
  "Peshawar",
  "Quetta",
  "Rahim Yar Khan",
  "Rajanpur",
  "Rawalakot",
  "Rawalpindi",
  "Rohri",
  "Sadiqabad",
  "Sahiwal",
  "Sanghar",
  "Sargodha",
  "Sheikhupura",
  "Shikarpur",
  "Sialkot",
  "Sibi",
  "Skardu",
  "Sukkur",
  "Swabi",
  "Swat",
  "Talagang",
  "Tando Adam",
  "Tando Allahyar",
  "Tando Muhammad Khan",
  "Taxila",
  "Thatta",
  "Toba Tek Singh",
  "Turbat",
  "Umerkot",
  "Vehari",
  "Wah Cantt",
  "Wazirabad",
  "Zhob",
  "Ziarat",
];

const ShoppingCart = () => {
  const [loadingOrder, setLoadingOrder] = useState(false);
  const deliveryCost = 250;
  const dispatch = useDispatch();
  const cartItems = useSelector(
    (state: RootState) => state.cart.items,
  ) as CartItem[];

  const [orderCartItems, setOrderCartItems] = useState<CartItem[]>([]);
  const [payments, setPayments] = useState(false);
  const [activeTab, setActiveTab] = useState("cartTab1");

  // customer info
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("Pakistan");
  const [townCity, setTownCity] = useState("");
  const [lastName, setLastName] = useState("");
  const [postcode, setPostcode] = useState("");
  const [firstName, setFirstName] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [formError, setFormError] = useState("");
  const [customCity, setCustomCity] = useState(false);

  const itemsToShow =
    activeTab === "cartTab3" && orderCartItems.length > 0
      ? orderCartItems
      : cartItems;

  const getDiscountedPrice = (item: CartItem) => {
    if (item.sale && item.sale > 0) {
      return item.productPrice - (item.productPrice * item.sale) / 100;
    }
    return item.productPrice;
  };

  const subtotal = itemsToShow.reduce(
    (acc, item) => acc + getDiscountedPrice(item) * item.quantity,
    0,
  );
  const totalPrice = subtotal;

  const formatLocation = () => {
    return `${streetAddress}, ${townCity}, ${postcode}, ${country}`;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    switch (name) {
      case "firstName":
        setFirstName(value);
        break;
      case "lastName":
        setLastName(value);
        break;
      case "country":
        setCountry(value);
        if (value !== "Pakistan") {
          setCustomCity(false);
        } else if (townCity && !PAKISTAN_CITIES.includes(townCity)) {
          setTownCity("");
          setCustomCity(false);
        }
        break;
      case "streetAddress":
        setStreetAddress(value);
        break;
      case "townCity":
        setTownCity(value);
        break;
      case "postcode":
        setPostcode(value);
        break;
      case "phone":
        setPhone(value);
        break;
      case "email":
        setEmail(value);
        break;
    }
  };

  const handleTabClick = (tab: string) => {
    if (tab === "cartTab1" || cartItems.length > 0) {
      setActiveTab(tab);
    }
  };

  const handleQuantityChange = (productId: number, quantity: number) => {
    if (quantity >= 1 && quantity <= 20) {
      dispatch(updateCartItemQuantity(productId, quantity));
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentDate = new Date();
  const formatDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const [orderId, setOrderId] = useState("");

  function handlePaymentChange() {
    setPayments(true);
  }

  const placeOrder = (e: React.FormEvent) => {
    e.preventDefault();
    const billing = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      country: country.trim(),
      street: streetAddress.trim(),
      city: townCity.trim(),
      zipCode: postcode.trim(),
      phone: phone.trim(),
      email: email.trim(),
    };
    const missing = [
      !billing.firstName && "first name",
      !billing.lastName && "last name",
      !billing.country && "country",
      !billing.street && "street address",
      !billing.city && "city",
      !billing.zipCode && "postcode",
      !billing.phone && "phone",
      !billing.email && "email",
    ].filter(Boolean);
    if (missing.length > 0) {
      setFormError(`Enter your ${missing.join(", ")}.`);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billing.email)) {
      setFormError("Enter a valid email address.");
      return;
    }

    setFormError("");
    setActiveTab("cartTab3");
    scrollToTop();
    setLoadingOrder(true);

    const orderedItems = cartItems.map((item) => ({
      ...item,
      color: item.color || "N/A",
      size: item.size || "No Size",
      quantity: Number(item.quantity) || 1,
      productPrice: Number(item.productPrice) || 0,
      sale: Number(item.sale) || 0,
    }));
    const orderTotal = totalPrice + deliveryCost;
    const order: Order = {
      status: OrderStatus.Pending,
      quantity: orderedItems.reduce((acc, item) => acc + item.quantity, 0),
      totalPrice: orderTotal,
      items: orderedItems,
      createdAt: currentDate,
      updatedAt: currentDate,
      customerInfo: {
        id: "",
        firstName: billing.firstName,
        lastName: billing.lastName,
        phoneNumber: billing.phone,
        email: billing.email,
        address: {
          country: billing.country,
          street: billing.street,
          city: billing.city,
          zipCode: billing.zipCode,
        },
      },
      id: "",
    };

    createOrder(order).then(async (response) => {
      if (!response.success || !response.id) {
        setLoadingOrder(false);
        setActiveTab("cartTab2");
        setFormError(
          response.error?.message ||
            "Could not place the order. Please try again.",
        );
        return;
      }

      setOrderId(response.id);
      setOrderCartItems(orderedItems);
      setPayments(true);
      dispatch({ type: "CLEAR_CART" });
      setLoadingOrder(false);
      scrollToTop();

      try {
        await fetch(`${process.env.REACT_APP_SUPABASE_FUNCTION_URL}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            orderId: response.id,
            customerInfo: {
              firstName: order.customerInfo.firstName,
              lastName: order.customerInfo.lastName,
              address: order.customerInfo.address,
              email: order.customerInfo.email,
              phoneNumber: order.customerInfo.phoneNumber,
            },
            products: orderedItems.map((item) => ({
              frontImage: item.frontImg,
              description: item.productName,
              quantity: item.quantity,
              price: item.productPrice,
              sale: item.sale || 0,
              size: item.size,
              color: item.color,
            })),
          }),
        });
      } catch (error) {
        console.error("Failed to send confirmation email:", error);
      }
    });
  };

  return (
    <div className="az cartPage">
      <div className="az-wrap shoppingCartSection">
        <div className="crumbs">
          <Link to="/" onClick={scrollToTop}>
            Home
          </Link>
          <span>/</span>
          <span>{orderId ? "Order" : "Cart"}</span>
        </div>
        {!orderId && <h2 className="serif">Cart</h2>}
        {orderId && <h2 className="serif">Order received</h2>}

        <div className="shoppingCartTabsContainer">
          {/* Tabs */}
          {!orderId && cartItems.length > 0 && (
            <div className={`shoppingCartTabs ${activeTab}`}>
              <button
                className={activeTab === "cartTab1" ? "active" : ""}
                onClick={() => handleTabClick("cartTab1")}
              >
                <div className="shoppingCartTabsNumber">
                  <h3>01</h3>
                  <div className="shoppingCartTabsHeading">
                    <h3>Shopping Bag</h3>
                    <p>Manage Your Items List</p>
                  </div>
                </div>
              </button>

              <button
                className={activeTab === "cartTab2" ? "active" : ""}
                onClick={() => handleTabClick("cartTab2")}
                disabled={cartItems.length === 0}
              >
                <div className="shoppingCartTabsNumber">
                  <h3>02</h3>
                  <div className="shoppingCartTabsHeading">
                    <h3>Shipping and Checkout</h3>
                    <p>Checkout Your Items List</p>
                  </div>
                </div>
              </button>

              <button
                className={activeTab === "cartTab3" ? "active" : ""}
                onClick={() => handleTabClick("cartTab3")}
                disabled={cartItems.length === 0 || !payments}
              >
                <div className="shoppingCartTabsNumber">
                  <h3>03</h3>
                  <div className="shoppingCartTabsHeading">
                    <h3>Confirmation</h3>
                    <p>Review And Submit Your Order</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Content */}
          <div className="shoppingCartTabsContent">
            {/* tab1 */}
            {activeTab === "cartTab1" && cartItems.length === 0 && (
              <div className="shoppingCartEmpty">
                <span>Your cart is empty!</span>
                <Link to="/shop" onClick={scrollToTop}>
                  <button>Shop Now</button>
                </Link>
              </div>
            )}
            {activeTab === "cartTab1" && cartItems.length > 0 && (
              <div className="shoppingBagSection">
                <div className="shoppingBagTableSection">
                  <table className="shoppingBagTable">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Product Name</th>
                        <th>Price</th>
                        <th>Color</th>
                        <th>Size</th>
                        <th>Quantity</th>
                        <th>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      <>
                          {/* Desktop View */}
                          {cartItems.map((item) => {
                            const discounted = getDiscountedPrice(item);
                            return (
                              <tr key={item.id}>
                                <td data-label="Product">
                                  <div className="shoppingBagTableImg">
                                    <Link to="/product" onClick={scrollToTop}>
                                      <img src={item.frontImg} alt="" />
                                    </Link>
                                  </div>
                                </td>
                                <td data-label="">
                                  <div className="shoppingBagTableProductDetail">
                                    <Link to="/product" onClick={scrollToTop}>
                                      <h4>{item.productName}</h4>
                                    </Link>
                                  </div>
                                </td>
                                <td data-label="Price">
                                  <span className="bagPrice">
                                    Rs {discounted}
                                    {item.sale && item.sale > 0 ? (
                                      <s>Rs {item.productPrice}</s>
                                    ) : null}
                                  </span>
                                </td>
                                <td data-label="Color">
                                  <span
                                    className="swatch"
                                    style={{ background: item.color }}
                                    title={item.color}
                                  />
                                </td>
                                <td data-label="Size">
                                  {item.size || "No Size"}
                                </td>
                                <td data-label="Quantity">
                                  <div className="ShoppingBagTableQuantity">
                                    <button
                                      onClick={() =>
                                        handleQuantityChange(
                                          Number(item.id),
                                          item.quantity - 1,
                                        )
                                      }
                                    >
                                      -
                                    </button>
                                    <input
                                      type="text"
                                      min="1"
                                      max="20"
                                      value={item.quantity}
                                      onChange={(e) =>
                                        handleQuantityChange(
                                          Number(item.id),
                                          parseInt(e.target.value),
                                        )
                                      }
                                    />
                                    <button
                                      onClick={() =>
                                        handleQuantityChange(
                                          Number(item.id),
                                          item.quantity + 1,
                                        )
                                      }
                                    >
                                      +
                                    </button>
                                  </div>
                                </td>
                                <td data-label="Subtotal">
                                  <p className="subtotal-price">
                                    Rs {item.quantity * discounted}
                                  </p>
                                </td>
                                <td data-label="" className="close-icon">
                                  <MdOutlineClose
                                    onClick={() =>
                                      dispatch(removeFromCart(item.id))
                                    }
                                  />
                                </td>
                              </tr>
                            );
                          })}

                          {/* Mobile View */}
                          <tr className="shoppingBagTableMobile">
                            <td colSpan={8}>
                              {cartItems.map((item) => {
                                const discounted = getDiscountedPrice(item);
                                return (
                                  <div
                                    className="shoppingBagTableMobileItems"
                                    key={item.id}
                                  >
                                    <div className="shoppingBagTableMobileItemsImg">
                                      <Link to="/product" onClick={scrollToTop}>
                                        <img src={item.frontImg} alt="" />
                                      </Link>
                                    </div>
                                    <div className="shoppingBagTableMobileItemsDetail">
                                      <div className="shoppingBagTableMobileItemsDetailMain">
                                        <Link
                                          to="/product"
                                          onClick={scrollToTop}
                                        >
                                          <h4>{item.productName}</h4>
                                        </Link>
                                        <p>
                                          {item.size
                                            ? `Size: ${item.size}`
                                            : ""}
                                        </p>
                                        <div className="shoppingBagTableMobileQuantity">
                                          <button
                                            onClick={() =>
                                              handleQuantityChange(
                                                Number(item.id),
                                                item.quantity - 1,
                                              )
                                            }
                                          >
                                            -
                                          </button>
                                          <input
                                            type="text"
                                            min="1"
                                            max="20"
                                            value={item.quantity}
                                            onChange={(e) =>
                                              handleQuantityChange(
                                                Number(item.id),
                                                parseInt(e.target.value),
                                              )
                                            }
                                          />
                                          <button
                                            onClick={() =>
                                              handleQuantityChange(
                                                Number(item.id),
                                                item.quantity + 1,
                                              )
                                            }
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                      <div className="shoppingBagTableMobileItemsDetailTotal">
                                        <span
                                          className="swatch"
                                          style={{ background: item.color }}
                                          title={item.color}
                                        />
                                        <p className="bagPrice">
                                          Rs {discounted}
                                          {item.sale && item.sale > 0 ? (
                                            <s>Rs {item.productPrice}</s>
                                          ) : null}
                                        </p>
                                        <p className="subtotal-price">
                                          Total: Rs {item.quantity * discounted}
                                        </p>
                                        <MdOutlineClose
                                          onClick={() =>
                                            dispatch(removeFromCart(item.id))
                                          }
                                          style={{ fontSize: "20px" }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </td>
                          </tr>
                      </>
                    </tbody>
                    <tfoot></tfoot>
                  </table>
                </div>
                <div className="shoppingBagTotal">
                  <table className="shoppingBagTotalTable">
                    <tbody>
                      <tr>
                        <th>Subtotal</th>
                        <th>Delivery</th>
                        <th>Total</th>
                      </tr>
                      <tr>
                        <td>Rs {totalPrice}</td>
                        <td>Rs {deliveryCost.toFixed(2)}</td>
                        <td>
                          Rs{" "}
                          {(totalPrice === 0
                            ? 0
                            : totalPrice + deliveryCost
                          ).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <button
                    onClick={() => {
                      handleTabClick("cartTab2");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    disabled={cartItems.length === 0}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            )}

            {/* tab2 */}
            {activeTab === "cartTab2" && (
              <div className="checkoutSection">
                <div className="checkoutDetailsSection">
                  <h4>Billing Details</h4>
                  <div className="checkoutDetailsForm">
                    <form onSubmit={placeOrder} noValidate>
                      <div className="checkoutDetailsFormRow">
                        <div className="field">
                          <label htmlFor="firstName">First name</label>
                          <input
                            id="firstName"
                            type="text"
                            name="firstName"
                            value={firstName}
                            onChange={handleChange}
                            autoComplete="given-name"
                            placeholder="Ayesha"
                          />
                        </div>
                        <div className="field">
                          <label htmlFor="lastName">Last name</label>
                          <input
                            id="lastName"
                            type="text"
                            name="lastName"
                            value={lastName}
                            onChange={handleChange}
                            autoComplete="family-name"
                            placeholder="Khan"
                          />
                        </div>
                      </div>
                      <div className="checkoutDetailsFormRow">
                        <div className="field">
                          <label htmlFor="phone">Phone</label>
                          <input
                            id="phone"
                            type="tel"
                            name="phone"
                            value={phone}
                            onChange={handleChange}
                            autoComplete="tel"
                            placeholder="03XX XXXXXXX"
                          />
                        </div>
                        <div className="field">
                          <label htmlFor="email">Email</label>
                          <input
                            id="email"
                            type="email"
                            name="email"
                            value={email}
                            onChange={handleChange}
                            autoComplete="email"
                            placeholder="you@email.com"
                          />
                        </div>
                      </div>
                      <div className="field">
                        <label htmlFor="country">Country</label>
                        <select
                          id="country"
                          name="country"
                          value={country}
                          onChange={handleChange}
                          autoComplete="country-name"
                        >
                          <option value="">Select country</option>
                          <option value="Pakistan">Pakistan</option>
                          <option value="India">India</option>
                          <option value="Saudi Arabia">Saudi Arabia</option>
                          <option value="Dubai">Dubai</option>
                        </select>
                      </div>
                      <div className="field">
                        <label htmlFor="streetAddress">Street address</label>
                        <input
                          id="streetAddress"
                          type="text"
                          name="streetAddress"
                          value={streetAddress}
                          onChange={handleChange}
                          autoComplete="street-address"
                          placeholder="House, street, area"
                        />
                      </div>
                      <div className="checkoutDetailsFormRow">
                        <div className="field">
                          <label htmlFor="townCity">City</label>
                          {country === "Pakistan" ? (
                            <select
                              id="townCity"
                              value={
                                customCity
                                  ? "Other"
                                  : PAKISTAN_CITIES.includes(townCity)
                                    ? townCity
                                    : ""
                              }
                              onChange={(event) => {
                                const value = event.target.value;
                                if (value === "Other") {
                                  setCustomCity(true);
                                  setTownCity("");
                                  return;
                                }
                                setCustomCity(false);
                                setTownCity(value);
                              }}
                            >
                              <option value="">Select city</option>
                              {PAKISTAN_CITIES.map((city) => (
                                <option key={city} value={city}>
                                  {city}
                                </option>
                              ))}
                              <option value="Other">Other</option>
                            </select>
                          ) : (
                            <input
                              id="townCity"
                              type="text"
                              name="townCity"
                              value={townCity}
                              onChange={handleChange}
                              autoComplete="address-level2"
                              placeholder="City"
                            />
                          )}
                          {country === "Pakistan" && customCity && (
                            <input
                              type="text"
                              name="townCity"
                              value={townCity}
                              onChange={handleChange}
                              placeholder="Enter city"
                            />
                          )}
                        </div>
                        <div className="field">
                          <label htmlFor="postcode">Postcode</label>
                          <input
                            id="postcode"
                            type="text"
                            name="postcode"
                            value={postcode}
                            onChange={handleChange}
                            autoComplete="postal-code"
                            placeholder="46000"
                          />
                        </div>
                      </div>
                      {formError && <p className="fieldError">{formError}</p>}
                      <button type="submit">Place Order</button>
                    </form>
                  </div>
                </div>
                <div className="checkoutPaymentSection">
                  <div className="checkoutTotalContainer">
                    <h3>Your Order</h3>
                    <div className="checkoutItems">
                      <table>
                        <thead>
                          <tr>
                            <th>PRODUCTS</th>
                            <th>SUBTOTALS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cartItems.map((item) => {
                            const discounted = getDiscountedPrice(item);
                            return (
                              <tr key={item.id}>
                                <td>
                                  {item.productName} x {item.quantity}
                                </td>
                                <td>Rs {item.quantity * discounted}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="checkoutTotal">
                      <table>
                        <tbody>
                          <tr>
                            <th>Subtotal</th>
                            <td>Rs {totalPrice.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <th>Delivery</th>
                            <td>Rs {deliveryCost.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <th>Total</th>
                            <td>
                              Rs{" "}
                              {(totalPrice === 0
                                ? 0
                                : totalPrice + deliveryCost
                              ).toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="checkoutPaymentContainer">
                    <label>
                      <input
                        type="radio"
                        name="payment"
                        value="Cash on delivery"
                        onChange={handlePaymentChange}
                        defaultChecked
                      />
                      <div className="checkoutPaymentMethod">
                        <span>Cash on delivery: Rs 250</span>
                        <p>
                          Cash on Delivery is the only payment option available
                          at the moment. Pay when your order is delivered to
                          your doorstep. No advance payment required.
                        </p>
                      </div>
                    </label>
                    <div className="policyText">
                      Your personal data will be used to process your order,
                      support your experience throughout this website, and for
                      other purposes described in our{" "}
                      <Link to="/terms" onClick={scrollToTop}>
                        Privacy Policy
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* tab3 */}
            {activeTab === "cartTab3" &&
              (loadingOrder ? (
                <BarSpinner className="loader"></BarSpinner>
              ) : (
                <div className="orderCompleteSection">
                  <div className="orderComplete">
                    <div className="orderCompleteMessage">
                      <div className="orderCompleteMessageImg">
                        <img src="/assets/success.png" alt="Success" />
                      </div>
                      <h3>Your order is completed!</h3>
                      <p>Thank you. Your order has been received.</p>
                    </div>
                    <div
                      className="orderTotalContainer"
                      style={{ marginBottom: "30px" }}
                    >
                      <table
                        className="orderInfoTable"
                        style={{ borderCollapse: "collapse" }}
                      >
                        <tbody>
                          <tr>
                            <td
                              className="order-info-label"
                              style={{ padding: "18px 16px", fontWeight: 500 }}
                            >
                              Order ID
                            </td>
                            <td
                              className="order-info-value"
                              style={{ padding: "18px 16px" }}
                            >
                              {orderId}
                            </td>
                            <td
                              className="order-info-label"
                              style={{ padding: "18px 16px", fontWeight: 500 }}
                            >
                              Date
                            </td>
                            <td
                              className="order-info-value"
                              style={{ padding: "18px 16px" }}
                            >
                              {formatDate(currentDate)}
                            </td>
                          </tr>
                          <tr>
                            <td
                              className="order-info-label"
                              style={{
                                padding: "18px 16px",
                                fontWeight: 500,
                                fontSize: "15px",
                              }}
                            >
                              Total
                            </td>
                            <td
                              className="order-info-value"
                              style={{
                                padding: "18px 16px",
                                fontSize: "15px",
                              }}
                            >
                              Rs {(subtotal + deliveryCost).toFixed(2)}
                            </td>
                            <td
                              className="order-info-label"
                              style={{ padding: "18px 16px", fontWeight: 500 }}
                            >
                              Customer Name
                            </td>
                            <td
                              className="order-info-value"
                              style={{ padding: "18px 16px" }}
                            >
                              {firstName} {lastName}
                            </td>
                          </tr>
                          <tr>
                            <td
                              className="order-info-label"
                              style={{ padding: "18px 16px", fontWeight: 500 }}
                            >
                              Contact Number
                            </td>
                            <td
                              className="order-info-value"
                              style={{ padding: "18px 16px" }}
                            >
                              {phone}
                            </td>
                            <td
                              className="order-info-label"
                              style={{ padding: "18px 16px", fontWeight: 500 }}
                            >
                              Location
                            </td>
                            <td
                              className="order-info-value"
                              style={{ padding: "18px 16px" }}
                            >
                              {formatLocation()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="orderTotalContainer">
                      <h3>Order Details</h3>
                      <div className="orderItems">
                        <table>
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Front Image</th>
                              <th>Quantity</th>
                              <th>Total Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itemsToShow.map((item) => {
                              const discounted = getDiscountedPrice(item);
                              return (
                                <tr key={item.id}>
                                  <td>{item.productName}</td>
                                  <td>
                                    {item.frontImg ? (
                                      <img
                                        src={item.frontImg}
                                        alt={item.productName}
                                        style={{
                                          width: "60px",
                                          height: "60px",
                                          objectFit: "cover",
                                          borderRadius: "8px",
                                        }}
                                      />
                                    ) : (
                                      <span>No image</span>
                                    )}
                                  </td>
                                  <td>{item.quantity}</td>
                                  <td>
                                    <span className="bagPrice">
                                      Rs {discounted * item.quantity}
                                      {item.sale && item.sale > 0 ? (
                                        <s>
                                          Rs {item.productPrice * item.quantity}
                                        </s>
                                      ) : null}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="orderTotal">
                        <table>
                          <tbody>
                            <tr>
                              <th>Subtotal</th>
                              <td>Rs {totalPrice.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <th>Delivery</th>
                              <td>Rs {deliveryCost.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <th>Total</th>
                              <td>Rs {(subtotal + deliveryCost).toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "30px",
                      }}
                    >
                      <Link
                        to="/shop"
                        onClick={scrollToTop}
                        className="cartBack"
                      >
                        <button type="button">Continue shopping</button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShoppingCart;
